import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API } from "@/lib/api";

/*  /stream/overlay/{code} — the OBS Browser Source overlay.
    Full-bleed 1920x1080 broadcast surface. No site chrome, no scroll.
    Dark background so OBS chroma-key or overlay-blend works cleanly.
    Polls /streamer/live/{code}/status every 2.5s for live tallies.

    Streamers drop this URL into OBS → Browser Source. That's the whole
    install step.
*/

const StreamOverlay = () => {
  const { code } = useParams();
  const [data, setData] = useState(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!code) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/streamer/live/${code}/status`);
        if (!cancelled) setData(data);
      } catch {}
    };
    poll();
    const id = setInterval(poll, 2500);
    const t  = setInterval(() => setTick((n) => n + 1), 1000);
    return () => { cancelled = true; clearInterval(id); clearInterval(t); };
  }, [code]);

  // elapsed time string
  const elapsed = (() => {
    if (!data?.live || !data.started_at) return "00:00";
    const ms = Date.now() - new Date(data.started_at).getTime();
    const total = Math.max(0, Math.floor(ms / 1000));
    const mm = String(Math.floor(total / 60)).padStart(2, "0");
    const ss = String(total % 60).padStart(2, "0");
    return `${mm}:${ss}`;
    // tick unused on purpose — React re-renders every second via setTick
  })();
  void tick;

  if (!data) return (
    <div className="min-h-screen w-screen bg-[#0a0908] flex items-center justify-center">
      <div className="text-[#5a5050] text-[10px] tracking-[0.3em]">◢ connecting to wasntme comms...</div>
    </div>
  );

  if (!data.live) return (
    <div className="min-h-screen w-screen bg-[#0a0908] text-[#f4f0ec] relative overflow-hidden" data-testid="overlay-idle">
      {/* Standby overlay — streamer hasn't started a case yet */}
      <div className="absolute top-6 left-6 flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-[#5a5050]" />
        <div className="text-[10px] tracking-[0.4em] text-[#5a5050]">COMMS STANDBY</div>
      </div>
      <div className="absolute top-6 right-6 text-[10px] tracking-[0.3em] text-[#d4af37]">
        WASNTME.PARTY · @{data.streamer?.handle}
      </div>
      <div className="h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-[11px] tracking-[0.5em] text-[#8b0000] mb-3">◉ READY FOR TRANSMISSION</div>
          <div className="font-display text-5xl text-[#f4f0ec] mb-2">Pick your case.</div>
          <div className="text-sm text-[#a39797]">
            Open the control panel and go live.
          </div>
        </div>
      </div>
    </div>
  );

  const total = data.total_votes || 0;
  const top3  = (data.suspects || []).slice(0, 3);

  return (
    <div className="min-h-screen w-screen bg-[#0a0908] text-[#f4f0ec] relative overflow-hidden" data-testid="overlay-live">
      {/* Scanline + grain for broadcast texture */}
      <div className="transmission-scanline absolute inset-0 pointer-events-none" aria-hidden="true" />
      <div className="transmission-grain  absolute inset-0 pointer-events-none" aria-hidden="true" />

      {/* Top bar — LIVE badge + case title + timer */}
      <div className="absolute top-6 left-6 right-6 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-2.5 h-2.5 rounded-full bg-[#8b0000] dispatcher-pulse" />
            <div className="text-[10px] tracking-[0.4em] text-[#8b0000]">LIVE · WASNTME CASE</div>
          </div>
          <div className="font-display text-4xl text-[#f4f0ec] leading-tight">{data.case.title}</div>
          <div className="text-xs text-[#a39797] mt-1">CASE #{data.case.number}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-1">ELAPSED</div>
          <div className="font-display text-5xl text-[#d4af37] tracking-widest">{elapsed}</div>
        </div>
      </div>

      {/* Center — top 3 suspects with live vote bars */}
      <div className="absolute left-6 right-6 top-1/2 -translate-y-1/2">
        <div className="text-[10px] tracking-[0.4em] text-[#d4af37] mb-4 text-center">◢ CHAT IS VOTING ◣</div>
        <div className="max-w-2xl mx-auto space-y-3">
          {top3.map((s, i) => (
            <div key={s.key} className="relative">
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-sm text-[#f4f0ec] font-medium">
                  <span className="text-[#d4af37] mr-2">{String(i + 1).padStart(2, "0")}</span>
                  {s.title}
                </div>
                <div className="text-xs text-[#a39797]">
                  {s.votes} <span className="text-[#5a5050]">·</span> {s.percent}%
                </div>
              </div>
              <div className="h-2.5 bg-[#1a1515] border border-[#2a2525] overflow-hidden">
                <div
                  className="h-full transition-all duration-500"
                  style={{
                    width: `${total ? Math.max(2, s.percent) : 0}%`,
                    background: i === 0 ? "#d4af37" : i === 1 ? "#8b0000" : "#5a5050",
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom bar — chat landing URL + referral QR + viewer tally */}
      <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] text-[#a39797] mb-1">VOTE IN YOUR BROWSER</div>
          <div className="font-display text-2xl text-[#d4af37] tracking-wide">
            wasntme.party/live/{data.streamer?.code}
          </div>
          <div className="text-[10px] text-[#5a5050] mt-1">
            🔴 {total} votes · 👁 {data.viewers_count || 0} watching · 💰 {data.buys_count || 0} packs sold
          </div>
        </div>
        <div className="text-right">
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-1">RUN BY</div>
          <div className="text-xl font-display text-[#f4f0ec]">@{data.streamer?.handle}</div>
          <div className="text-[10px] text-[#5a5050] mt-1">15% of every pack sold tonight</div>
        </div>
      </div>
    </div>
  );
};

export default StreamOverlay;
